'use client'

import { useState } from 'react'
import { Utils } from 'src/lib/utils'
import { useNotification, useSentRecipients } from '.'
import { InviteController } from '@/controllers'
import { InviteType, SendingDto, TransportType } from '@/interfaces/invite'
import { useParticipants } from '@/hooks/useParticipants'
import { SentRecipient } from '@/hooks/useSentRecipients'
import { NotificationType } from '@/components/ui'

const ONE_DAY = 1000 * 60 * 60 * 24

export interface FormValues {
  name: string
  address: string
  message?: string
  type: TransportType
}

export const useInvite = () => {
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<number[]>([])
  const [isVisibleModalInvitationList, setVisibilityModalInvitationList] = useState<boolean>(false)
  const { openNotification, message } = useNotification()
  const { participants: participantList } = useParticipants()
  const { recipients, addRecipient } = useSentRecipients()

  const preparePayload = (data: FormValues): SendingDto => ({
    message: data.message,
    recipients: [
      {
        type: data.type,
        name: data.name,
        address: data.address,
      },
    ],
    participants: selectedParticipantIds,
  })

  const saveRecipientInStorage = (data: FormValues) => {
    const participants = participantList.filter((participant) => selectedParticipantIds.includes(participant.id))
    const newInvite: SentRecipient = {
      name: data.name,
      address: data.address,
      participants,
      type: InviteType.INVITE,
      createdAt: new Date().toString(),
    }

    addRecipient(newInvite)
  }

  const getFormattedAddress = (address: string, type: TransportType) =>
    type === TransportType.EMAIL ? Utils.toLowerCase(address) : Utils.formatPhoneNumber(address)

  const canSendInvite = (data: FormValues) => {
    for (const invite of recipients) {
      const now = Date.now()
      const createdAt = +new Date(invite.createdAt)
      const difference = now - createdAt
      const inviteAddress = getFormattedAddress(invite.address, data.type)
      const dataAddress = getFormattedAddress(data.address, data.type)
      if (inviteAddress === dataAddress && difference < ONE_DAY) {
        const setOfParticipantIds = new Set(invite.participants.map((participant) => participant.id))
        const hasNewParticipant = selectedParticipantIds.some((id) => !setOfParticipantIds.has(id))
        if (!hasNewParticipant) return false
      }
    }
    return true
  }

  const sendInvite = async (data: FormValues): Promise<boolean> => {
    if (selectedParticipantIds.length === 0) {
      openNotification(message.selectOneParticipant, NotificationType.WARNING)
      return false
    }
    if (!canSendInvite(data)) {
      openNotification(message.cantSendInvite(data), NotificationType.WARNING)
      return false
    }
    const {
      result: [error],
    } = await InviteController.send(preparePayload(data))
    if (error) {
      openNotification(error, NotificationType.ERROR)
      return false
    }
    openNotification(message.invitationSent(data.name), NotificationType.SUCCESS)
    return true
  }

  const hideInvitationListModal = () => setVisibilityModalInvitationList(false)
  const showInvitationListModal = () => setVisibilityModalInvitationList(true)

  return {
    selectedParticipantIds,
    setSelectedParticipantIds,
    isVisibleModalInvitationList,
    showInvitationListModal,
    hideInvitationListModal,
    sendInvite,
    saveRecipientInStorage,
  }
}
